import { Fragment, useCallback, useMemo } from 'react';
import { __CERTIFICATION_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import {
  FiltersCertificate,
  FiltersCertificatePerson,
} from 'front-office/components/released-services/list';
import { ReleasedService } from 'shell/models/certificate';
import { generatePath, useNavigate } from 'react-router-dom';
import { HttpResponse, HttpResponseList } from 'shell/models/http-response';
import { useFormik } from 'formik';
import { initialValues } from '..';
import { useEffect } from 'react';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonCertificateCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { IconButton, Tooltip, Typography } from '@mui/material';
import {
  FiltersFormCertificate,
  CertificateClient,
  CertificateResponse,
  SearchType,
} from './types';
import { transformDataset } from './utils';
import { authsObj, headCells, validationSchemaCertificateFilter } from './constants';
import { transformCertificateList } from 'front-office/components/released-services/utils/transform-certificate-list';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { CsvExtraction } from 'shell/components/shared/csv-extraction';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { DateTime } from 'luxon';
import { base64ToBlob } from 'shell/utils/base64-to-blob';
import { downloadBlob } from 'front-office/utils/download-blob';
import { ResponseCsv } from 'shell/models/response-csv';
import { ServiceType } from 'shell/constants/service-type';
import { Edit, Visibility as VisibilityIcon, Delete } from '@mui/icons-material';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';

const CertificatePage = (): React.ReactElement => {
  const filterTable = useFilterTable<FiltersFormCertificate>();

  const { userProfile } = useUserProfile();

  const [certificates, getCertificates] = useHttpFetch<
    HttpResponseList<ReleasedService>,
    CertificateResponse
  >(`${__CERTIFICATION_API_URL__}/certificazione/registro-doc-rilasciati`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformCertificateList,
  });

  const onSubmit = useCallback(
    (formValues: FiltersFormCertificate) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterCertificateForm.setFieldValue('page', 0);
      getCertificates({
        params: {},
        paramsSerializer: () => queryString,
      });
    },
    [getCertificates]
  );

  const controlloDiCassa: string = useMemo(() => {
    return userProfile?.datiLogin?.profile?.feature?.controlloDiCassa || '';
  }, [userProfile?.datiLogin?.profile?.feature?.controlloDiCassa]);

  const filterCertificateForm = useFormik<FiltersFormCertificate>({
    initialValues: {
      ...initialValues,
      service:
        controlloDiCassa === '0' || controlloDiCassa === '1'
          ? userProfile.datiLogin?.profile?.servizio?.key?.codiceServizio
          : undefined,
      serviceDesc:
        controlloDiCassa === '0' || controlloDiCassa === '1'
          ? userProfile.datiLogin?.profile?.servizio?.descrizione
          : undefined,
      operator: controlloDiCassa === '0' ? userProfile.datiLogin?.key?.userName : undefined,
      operatorDesc: controlloDiCassa === '0' ? userProfile.datiLogin?.nome : undefined,
      searchType: SearchType.Generic,
    },
    validationSchema: validationSchemaCertificateFilter,
    onSubmit,
  });

  useEffect(() => {
    filterCertificateForm.setValues(filterTable.values || initialValues);
    const queryString = transformDataset(filterTable.values || initialValues);
    getCertificates({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterCertificateForm.values);
  }, [filterCertificateForm.values]);

  const navigate = useNavigate();

  const active = useActionAuthorization(authsObj);

  const onVisibilityPress = useCallback(
    (cell): Promise<CertificateClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sportello/controllo-di-cassa/dettaglio/:idSigla/:idAnno/:progressivo', {
            idSigla: cell.key.codiceSiglaRegistro,
            idAnno: cell.key.idAnnoDocumento,
            progressivo: cell.key.progressivoDocumento,
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<CertificateClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sportello/controllo-di-cassa/modifica/:idSigla/:idAnno/:progressivo', {
            idSigla: cell.key.codiceSiglaRegistro,
            idAnno: cell.key.idAnnoDocumento,
            progressivo: cell.key.progressivoDocumento,
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<CertificateClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sportello/controllo-di-cassa/annullamento/:idSigla/:idAnno/:progressivo', {
            idSigla: cell.key.codiceSiglaRegistro,
            idAnno: cell.key.idAnnoDocumento,
            progressivo: cell.key.progressivoDocumento,
          })
        );
        resolve(cell);
      }) as Promise<CertificateClient>;
    },
    [navigate]
  );

  const onRestoreFromTrash = useCallback(
    (cell): Promise<CertificateClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sportello/controllo-di-cassa/ripristina/:idSigla/:idAnno/:progressivo', {
            idSigla: cell.key.codiceSiglaRegistro,
            idAnno: cell.key.idAnnoDocumento,
            progressivo: cell.key.progressivoDocumento,
          })
        );
        resolve(cell);
      }) as Promise<CertificateClient>;
    },
    [navigate]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      const newDataset = {
        ...filterCertificateForm.values,
        page: newPage,
      };

      const queryString = transformDataset(newDataset);

      (
        getCertificates({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<void, void>
      ).then(() => {
        filterCertificateForm.setFieldValue('page', newPage);
      });
    },
    [filterCertificateForm.setFieldValue, filterCertificateForm.values, getCertificates]
  );

  const body = useMemo(() => {
    if (!certificates.data) {
      return [];
    }

    return ((certificates.data as CertificateResponse)?.list || []).map((certificate) => {
      // Certificato annullato
      const isCanceled =
        certificate.item?.codiceStato === '3' || certificate.item?.codiceStato === '4';

      const isFirstCheckDelete =
        certificate.type === 'CIA' ||
        isCanceled ||
        certificate.typeCode === ServiceType.CartaElettronica || // carta elettronica
        certificate.typeCode === ServiceType.CartaCartacea; // carta cartacea;

      let hiddenDelete = false;

      if (isFirstCheckDelete) {
        if (certificate.typeCode === ServiceType.CartaElettronica) {
          if (isCanceled) {
            hiddenDelete = true;
          } else if (controlloDiCassa !== '2') {
            hiddenDelete = controlloDiCassa !== '2';
          } else {
            hiddenDelete = false;
          }
        } else if (
          (certificate.typeCode === ServiceType.CartaCartacea &&
            controlloDiCassa !== '2' &&
            userProfile?.datiLogin?.key?.userName !== certificate.releaseOperatorCode) ||
          isCanceled
        ) {
          hiddenDelete = true;
        } else if (certificate.item?.intestatarioResidente === '1') {
          hiddenDelete = true;
        } else {
          hiddenDelete = false;
        }
      }

      hiddenDelete = isFirstCheckDelete
        ? hiddenDelete
        : (controlloDiCassa === '0' || controlloDiCassa === '1') &&
          certificate.releaseOperatorCode !== userProfile?.datiLogin?.key?.userName;

      return {
        ...certificate,
        action: (
          <div className="pmi-d-flex">
            <Fragment>
              <Tooltip
                arrow
                title={
                  active.read
                    ? 'Visualizza'
                    : "Non si ha l'autorizzazione per accedere a questa funzionalità"
                }
              >
                <span>
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      onVisibilityPress(certificate);
                    }}
                    aria-label="Visualizza elemento"
                    aria-haspopup="true"
                    disabled={!active.read}
                    style={{
                      padding: '4px',
                    }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                arrow
                title={
                  active.edit
                    ? 'Modifica'
                    : "Non si ha l'autorizzazione per accedere a questa funzionalità"
                }
              >
                <span>
                  <IconButton
                    aria-label="Modifica elemento"
                    aria-haspopup="true"
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      onEditPress(certificate);
                    }}
                    disabled={!active.edit}
                    style={{
                      padding: '4px',
                    }}
                  >
                    <Edit />
                  </IconButton>
                </span>
              </Tooltip>
              {!hiddenDelete ? (
                <Tooltip
                  arrow
                  title={
                    active.delete
                      ? 'Elimina'
                      : "Non si ha l'autorizzazione per accedere a questa funzionalità"
                  }
                >
                  <span>
                    <IconButton
                      aria-label="Elimina elemento"
                      aria-haspopup="true"
                      color="cdmBrand"
                      onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        onTrashPress(certificate);
                      }}
                      disabled={!active.delete}
                      style={{
                        padding: '4px',
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : undefined}
              {controlloDiCassa === '2' && isCanceled ? (
                <Tooltip arrow title="Ripristina">
                  <span>
                    <IconButton
                      aria-label="Ripristina elemento"
                      aria-haspopup="true"
                      color="cdmBrand"
                      onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        onRestoreFromTrash(certificate);
                      }}
                      style={{
                        padding: '4px',
                      }}
                    >
                      <RestoreFromTrashIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : undefined}
            </Fragment>
          </div>
        ),
      };
    });
  }, [certificates, controlloDiCassa, userProfile?.datiLogin?.key?.userName]);

  const getCsv = useHttpClient<HttpResponse<ResponseCsv>, HttpResponse<ResponseCsv>>(
    `${__CERTIFICATION_API_URL__}/certificazione/registro-doc-rilasciati/csv`,
    {
      transformer: (response) => response?.data,
    }
  );

  const toPrintCSV = useCallback(() => {
    const newDataset: FiltersFormCertificate = {
      acronym: filterCertificateForm.values.acronym,
      year: filterCertificateForm.values.year,
      progressive: filterCertificateForm.values.progressive,
      certificateType: filterCertificateForm.values.certificateType,
      release: filterCertificateForm.values.release,
      void: filterCertificateForm.values.void,
      payment: filterCertificateForm.values.payment,
      service:
        filterCertificateForm.values.service === 'ALL'
          ? undefined
          : filterCertificateForm.values.service,
      operator: filterCertificateForm.values.operator,
      dateFrom: filterCertificateForm.values.dateFrom,
      dateTo: filterCertificateForm.values.dateTo,
      dateFromVoid: filterCertificateForm.values.dateFromVoid,
      dateToVoid: filterCertificateForm.values.dateToVoid,
      fiscalCode: filterCertificateForm.values.fiscalCode,
      name: filterCertificateForm.values.name,
      surname: filterCertificateForm.values.surname,
    };

    const queryString = transformDataset(newDataset);

    (
      getCsv({
        params: {},
        paramsSerializer: () => queryString,
      }) as ResponsePromise<HttpResponse<ResponseCsv>, HttpResponse<ResponseCsv>>
    ).then((response) => {
      const responseCasted = response as HttpResponse<ResponseCsv>;
      const csv = responseCasted?.payload?.contenutoCsv;
      if (csv) {
        // download csv
        const blob = base64ToBlob(csv);
        downloadBlob(blob, `export-csv-${DateTime.now().toFormat('ddMMyyHHmmss')}.csv`);
      }
    });
  }, [filterCertificateForm.values, getCsv]);

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...filterCertificateForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };

      const queryString = transformDataset(newDataset);

      (
        getCertificates({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<void, void>
      ).then(() => {
        filterCertificateForm.setValues(newDataset);
      });
    },
    [filterCertificateForm.setFieldValue, filterCertificateForm.values]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterCertificateForm.values, orders };
      const queryString = transformDataset(newDataset);
      (
        getCertificates({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<void, void>
      ).then(() => {
        filterCertificateForm.setFieldValue('orders', orders);
      });
    },
    [filterCertificateForm.setFieldValue, getCertificates, filterCertificateForm.values]
  );

  return (
    <Fragment>
      <Breadcrumbs items={CommonCertificateCrumbs} />
      <Typography
        color="secondary.main"
        style={{
          fontSize: '26px',
        }}
      >
        Lista servizi rilasciati
      </Typography>
      <div className="pmi-my-3">
        <FiltersCertificate filterCertificateForm={filterCertificateForm} />
        <FiltersCertificatePerson filterCertificateForm={filterCertificateForm} />
      </div>
      {certificates.data ? (
        body.length > 0 ? (
          <>
            <div className="pmi-my-3">
              <CsvExtraction onClick={toPrintCSV} title="Lista_Certificazione" />
            </div>
            <Table
              onSort={onSort}
              head={headCells}
              orders={filterCertificateForm.values.orders || {}}
              page={filterCertificateForm.values.page || 0}
              rowsPerPage={filterCertificateForm.values.rowsPerPage}
              body={body}
              isServer
              onVisibilityPress={onVisibilityPress}
              onEditPress={onEditPress}
              onTrashPress={onTrashPress}
              count={(certificates.data as CertificateResponse)?.count || 0}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ariaLabel="Lista Servizi Emessi"
              isNotLayoutFixed
              hideButtons
            />
          </>
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

CertificatePage.displayName = 'Certificate page component';

export default CertificatePage;
